import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Loader2, Plus, Send, Trash2, X, Database, Sparkles } from "lucide-react";
import { API, auth, guarded, OFFICES, SCHOOL_STAGES, chipCls, inputCls, label, selectCls, btnPrimary, btnGhost } from "./shared";

const empty = { name: "", contact_person: "", email: "", phone: "", office: "", lab_size: "", stage: "survey" };

export default function Schools({ token, onAuthError }) {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [stageF, setStageF] = useState("");
  const [officeF, setOfficeF] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const [view, setView] = useState("cards");
  const [prospects, setProspects] = useState(null);
  const [kanbanItems, setKanbanItems] = useState(null);
  const [draftingId, setDraftingId] = useState(null);

  const loadProspects = useCallback(
    () =>
      guarded(async () => {
        const { data } = await axios.get(`${API}/schools/top-prospects?limit=50`, auth(token));
        setProspects(data);
      }, onAuthError),
    [token, onAuthError]
  );

  const loadKanban = useCallback(
    () =>
      guarded(async () => {
        const params = { page: 1, limit: 100, search, office: officeF };
        const { data } = await axios.get(`${API}/schools`, { ...auth(token), params });
        setKanbanItems(data.items);
      }, onAuthError),
    [token, onAuthError, search, officeF]
  );

  useEffect(() => {
    if (view === "prospects" && !prospects) loadProspects();
  }, [view, prospects, loadProspects]);

  useEffect(() => {
    if (view === "kanban") loadKanban();
  }, [view, loadKanban]);

  const load = useCallback(
    () =>
      guarded(async () => {
        const params = { page, limit: 25, search, stage: stageF, office: officeF };
        const { data } = await axios.get(`${API}/schools`, { ...auth(token), params });
        setItems(data.items);
        setMeta({ total: data.total, page: data.page, pages: data.pages });
      }, onAuthError),
    [token, onAuthError, page, search, stageF, officeF]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = (e) => {
    e.preventDefault();
    guarded(async () => {
      const body = { ...form, lab_size: form.lab_size === "" ? null : Number(form.lab_size) };
      await axios.post(`${API}/schools`, body, auth(token));
      setForm(empty);
      setShowForm(false);
      await load();
    }, onAuthError);
  };

  const patch = (id, body) =>
    guarded(async () => {
      await axios.patch(`${API}/schools/${id}`, body, auth(token));
      await load();
      if (view === "kanban") await loadKanban();
      if (view === "prospects") await loadProspects();
    }, onAuthError);

  const remove = (id) => {
    if (!window.confirm("Delete this school? This cannot be undone.")) return;
    guarded(async () => {
      await axios.delete(`${API}/schools/${id}`, auth(token));
      await load();
    }, onAuthError);
  };

  if (!items) {
    return (
      <div className="flex items-center gap-3 py-20 text-[#8A837B]">
        <Loader2 size={18} className="animate-spin" /> Loading schools…
      </div>
    );
  }

  return (
    <div data-testid="schools-tab">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <input
          data-testid="schools-search-input"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search schools…"
          className={`${inputCls} max-w-xs`}
        />
        <select
          data-testid="schools-filter-office"
          value={officeF}
          onChange={(e) => { setPage(1); setOfficeF(e.target.value); }}
          className={selectCls}
        >
          <option value="">All offices</option>
          {OFFICES.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <select
          data-testid="schools-filter-stage"
          value={stageF}
          onChange={(e) => { setPage(1); setStageF(e.target.value); }}
          className={selectCls}
        >
          <option value="">All stages</option>
          {SCHOOL_STAGES.map((s) => (
            <option key={s} value={s}>{label(s)}</option>
          ))}
        </select>
        <span className="text-xs text-[#8A837B]" data-testid="schools-count">
          {meta.total} school{meta.total === 1 ? "" : "s"}
        </span>
        <div className="flex border border-[#E6E2DC] bg-white" data-testid="schools-view-switcher">
          {[
            ["cards", "Cards"],
            ["kanban", "Kanban"],
            ["prospects", "Top Prospects"],
          ].map(([v, l]) => (
            <button
              key={v}
              data-testid={`schools-view-${v}`}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
                view === v ? "bg-[#581820] text-[#FAF8F5]" : "text-[#6B655F] hover:text-[#141210]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-3">
          <input
            type="file"
            accept=".csv"
            id="schools-csv-input"
            data-testid="schools-import-input"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const fd = new FormData();
              fd.append("file", f);
              await guarded(async () => {
                const { data } = await axios.post(`${API}/schools/import`, fd, auth(token));
                toast.success(`Import done — ${data.added} added, ${data.updated} updated.`);
                setPage(1);
                await load();
              }, onAuthError);
              e.target.value = "";
            }}
          />
          <button
            data-testid="schools-import-button"
            onClick={() => document.getElementById("schools-csv-input")?.click()}
            className={btnGhost}
          >
            <Database size={14} /> Import CSV
          </button>
          <button
            data-testid="school-load-pool-button"
            onClick={async () => {
              await guarded(async () => {
                const { data } = await axios.post(`${API}/schools/seed-pool`, {}, auth(token));
                toast.success(`School pool loaded — ${data.added} added, ${data.skipped} already present.`);
                await load();
              }, onAuthError);
            }}
            className={btnGhost}
          >
            <Database size={14} /> Load 20 Real Schools
          </button>
          <button
            data-testid="school-generate-mass-button"
            onClick={async () => {
              await guarded(async () => {
                const { data } = await axios.post(`${API}/schools/seed-mass`, {}, auth(token));
                toast.success(`Pool generated — ${data.added} schools added, ${data.skipped} already present.`);
                setPage(1);
                await load();
              }, onAuthError);
            }}
            className={btnGhost}
          >
            <Database size={14} /> Generate 1,000 Pool
          </button>
          <button data-testid="school-add-toggle" onClick={() => setShowForm(!showForm)} className={btnPrimary}>
            {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Close" : "Add School"}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={add} data-testid="school-form" className="mb-8 border border-[#E6E2DC] bg-white p-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <input data-testid="school-name-input" required value={form.name} onChange={set("name")} placeholder="School name *" className={inputCls} />
            <input data-testid="school-contact-input" value={form.contact_person} onChange={set("contact_person")} placeholder="Contact person" className={inputCls} />
            <input data-testid="school-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
            <input data-testid="school-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
            <select data-testid="school-office-select" value={form.office} onChange={set("office")} className={selectCls}>
              <option value="">Office…</option>
              {OFFICES.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <input data-testid="school-labsize-input" type="number" min="0" value={form.lab_size} onChange={set("lab_size")} placeholder="Lab size (seats)" className={inputCls} />
            <select data-testid="school-stage-select" value={form.stage} onChange={set("stage")} className={selectCls}>
              {SCHOOL_STAGES.map((s) => (
                <option key={s} value={s}>{label(s)}</option>
              ))}
            </select>
          </div>
          <button type="submit" data-testid="school-submit-button" className={`${btnPrimary} mt-6`}>
            <Plus size={14} /> Save School
          </button>
        </form>
      )}

      {view === "cards" && items.length === 0 && (
        <p className="border border-[#E6E2DC] bg-white p-8 text-sm text-[#8A837B]" data-testid="schools-empty">
          No schools match — adjust the filters or add a school.
        </p>
      )}
      {view === "cards" && (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((s) => {
          const open = openId === s.id;
          const MONO_COLORS = ["#581820", "#2D4A3E", "#1230AE", "#B45309", "#1E7A5A", "#6B4A2D"];
          const initials = s.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
          let hash = 0;
          for (const c of s.name) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
          return (
            <div key={s.id} className="flex flex-col border border-[#E6E2DC] bg-white" data-testid={`school-row-${s.id}`}>
              <div className="flex items-start gap-3 p-5">
                {s.logo_url ? (
                  <img
                    src={s.logo_url}
                    alt={`${s.name} logo`}
                    data-testid={`school-logo-${s.id}`}
                    className="h-11 w-11 shrink-0 border border-[#E6E2DC] bg-white object-contain"
                  />
                ) : (
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center font-display text-sm font-semibold text-[#FAF8F5]"
                    style={{ background: MONO_COLORS[hash % MONO_COLORS.length] }}
                    data-testid={`school-logo-${s.id}`}
                  >
                    {initials}
                  </span>
                )}
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-[#141210]" title={s.name}>{s.name}</h3>
                  <p className="mt-0.5 text-xs text-[#6B655F]">
                    {[s.city, s.region].filter(Boolean).join(", ") || s.contact_person || "—"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.school_type && (
                      <span className="border border-[#E6E2DC] px-2 py-0.5 text-[10px] font-semibold text-[#6B655F]">{s.school_type}</span>
                    )}
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${chipCls(s.stage)}`}>{label(s.stage)}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 px-5 text-[11px] text-[#8A837B]">
                {s.students != null && <span>~{s.students} students</span>}
                {s.lab_size != null && <span>{s.lab_size} lab seats</span>}
                <span>{s.office === "Abidjan office" ? "Abidjan" : s.office === "Tema office" ? "Tema" : s.office || "—"}</span>
              </div>
              <div className="mt-auto flex items-center gap-2 border-t border-[#F1EDE6] px-5 py-3">
                <select
                  data-testid={`school-stage-${s.id}`}
                  value={s.stage}
                  onChange={(e) => patch(s.id, { stage: e.target.value })}
                  className={`${selectCls} flex-1`}
                >
                  {SCHOOL_STAGES.map((st) => (
                    <option key={st} value={st}>{label(st)}</option>
                  ))}
                </select>
                <button
                  data-testid={`school-expand-${s.id}`}
                  onClick={() => setOpenId(open ? null : s.id)}
                  className="border border-[#DCD6CE] p-2 text-[#6B655F] transition-colors duration-300 hover:border-[#581820] hover:text-[#581820]"
                  aria-label="Details"
                >
                  <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                </button>
                <button
                  data-testid={`school-delete-${s.id}`}
                  onClick={() => remove(s.id)}
                  className="p-2 text-[#8A837B] transition-colors duration-300 hover:text-[#581820]"
                  aria-label="Delete school"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {open && (
                <div className="border-t border-[#F1EDE6] bg-[#FAF8F5] px-5 py-5" data-testid={`school-detail-${s.id}`}>
                  <form
                    data-testid={`school-edit-form-${s.id}`}
                    onSubmit={(e) => {
                      e.preventDefault();
                      const fd = new FormData(e.target);
                      const payload = {};
                      for (const k of ["contact_person", "email", "phone", "website", "logo_url", "city", "region", "school_type"]) {
                        const v = fd.get(k);
                        if (v !== null && v !== "") payload[k] = v;
                      }
                      for (const k of ["lab_size", "students"]) {
                        const v = fd.get(k);
                        if (v !== null && v !== "") payload[k] = Number(v);
                      }
                      patch(s.id, payload);
                    }}
                    className="grid grid-cols-2 gap-4"
                  >
                    <input name="contact_person" defaultValue={s.contact_person || ""} placeholder="Contact person" className={inputCls} data-testid={`school-edit-contact-${s.id}`} />
                    <input name="email" defaultValue={s.email || ""} placeholder="Email" className={inputCls} data-testid={`school-edit-email-${s.id}`} />
                    <input name="phone" defaultValue={s.phone || ""} placeholder="Phone" className={inputCls} data-testid={`school-edit-phone-${s.id}`} />
                    <input name="website" defaultValue={s.website || ""} placeholder="Website" className={inputCls} data-testid={`school-edit-website-${s.id}`} />
                    <input name="logo_url" defaultValue={s.logo_url || ""} placeholder="Logo image URL" className={inputCls} data-testid={`school-edit-logo-${s.id}`} />
                    <input name="city" defaultValue={s.city || ""} placeholder="City" className={inputCls} />
                    <input name="region" defaultValue={s.region || ""} placeholder="Region" className={inputCls} />
                    <input name="students" type="number" min="0" defaultValue={s.students ?? ""} placeholder="Students" className={inputCls} />
                    <input name="lab_size" type="number" min="0" defaultValue={s.lab_size ?? ""} placeholder="Lab seats" className={inputCls} />
                    <button type="submit" data-testid={`school-edit-save-${s.id}`} className={`${btnPrimary} col-span-2 justify-center`}>
                      Save Details
                    </button>
                  </form>
                  <div className="mt-5">
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A837B]">History Log</h4>
                    <div className="mt-2 flex max-h-32 flex-col gap-2 overflow-y-auto">
                      {(s.history || []).map((h, i) => (
                        <p key={i} className="text-xs text-[#141210]">
                          <span className="font-semibold">{h.by}</span> · {new Date(h.at).toLocaleString()} — {h.text}
                        </p>
                      ))}
                      {(s.history || []).length === 0 && <p className="text-xs text-[#8A837B]">No history yet.</p>}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <input
                        data-testid={`school-note-input-${s.id}`}
                        value={open ? note : ""}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Log a visit, install or call…"
                        className={inputCls}
                      />
                      <button
                        data-testid={`school-note-add-${s.id}`}
                        onClick={async () => {
                          if (!note.trim()) return;
                          await patch(s.id, { note: note.trim() });
                          setNote("");
                        }}
                        className="flex items-center gap-2 bg-[#141210] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#581820]"
                      >
                        <Send size={12} /> Log
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {view === "kanban" && (
        <div data-testid="schools-kanban" className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
          {!kanbanItems && (
            <div className="col-span-full flex items-center gap-3 py-16 text-[#8A837B]">
              <Loader2 size={18} className="animate-spin" /> Loading board…
            </div>
          )}
          {kanbanItems && SCHOOL_STAGES.map((st) => {
            const col = kanbanItems.filter((s) => s.stage === st);
            return (
              <div
                key={st}
                data-testid={`kanban-col-${st}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/plain");
                  const sch = kanbanItems.find((x) => x.id === id);
                  if (sch && sch.stage !== st) patch(id, { stage: st });
                }}
                className="min-h-[240px] border border-[#E6E2DC] bg-white/60 p-2.5"
              >
                <div className="mb-3 flex items-center justify-between px-1">
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${chipCls(st)}`}>{label(st)}</span>
                  <span className="text-[11px] text-[#8A837B]">{col.length}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {col.map((s) => (
                    <div
                      key={s.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", s.id)}
                      data-testid={`kanban-card-${s.id}`}
                      className="cursor-grab border border-[#E6E2DC] bg-white p-3 transition-colors duration-300 hover:border-[#581820] active:cursor-grabbing"
                    >
                      <p className="text-xs font-semibold text-[#141210]">{s.name}</p>
                      <p className="mt-0.5 text-[11px] text-[#8A837B]">
                        {[s.city, s.region].filter(Boolean).join(", ") || "—"}
                        {s.students != null ? ` · ~${s.students}` : ""}
                      </p>
                    </div>
                  ))}
                  {col.length === 0 && <p className="px-1 text-[11px] text-[#8A837B]">Drop schools here</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "prospects" && (
        <div data-testid="schools-prospects" className="flex flex-col gap-3">
          {!prospects && (
            <div className="flex items-center gap-3 py-16 text-[#8A837B]">
              <Loader2 size={18} className="animate-spin" /> Scoring 1,000+ schools…
            </div>
          )}
          {prospects && prospects.map((s, i) => {
            const t = (s.school_type || "").toLowerCase();
            const reasons = [];
            if (/(international|privée|private|privé)/.test(t)) reasons.push("Private / Intl");
            if (/(academy|stem)/.test(t)) reasons.push("Academy / STEM");
            if ((s.students || 0) >= 1000) reasons.push("1,000+ students");
            if (!s.lab_size) reasons.push("No lab yet");
            const MONO_COLORS = ["#581820", "#2D4A3E", "#1230AE", "#B45309", "#1E7A5A", "#6B4A2D"];
            const initials = s.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
            let hash = 0;
            for (const c of s.name) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
            return (
              <div key={s.id} className="flex flex-wrap items-center gap-4 border border-[#E6E2DC] bg-white px-5 py-4" data-testid={`prospect-row-${s.id}`}>
                <span className="w-8 font-display text-lg font-semibold text-[#8A837B]">{String(i + 1).padStart(2, "0")}</span>
                {s.logo_url ? (
                  <img src={s.logo_url} alt="" className="h-10 w-10 shrink-0 border border-[#E6E2DC] bg-white object-contain" />
                ) : (
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center font-display text-xs font-semibold text-[#FAF8F5]"
                    style={{ background: MONO_COLORS[hash % MONO_COLORS.length] }}
                  >
                    {initials}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#141210]">{s.name}</p>
                  <p className="text-[11px] text-[#8A837B]">
                    {[s.city, s.region].filter(Boolean).join(", ") || "—"} · {s.school_type || "School"}
                    {s.students != null ? ` · ~${s.students} students` : ""}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {reasons.map((r) => (
                      <span key={r} className="bg-[#2D4A3E]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2D4A3E]">{r}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-20 bg-[#F1EDE6]">
                    <div className="h-1.5 bg-[#581820]" style={{ width: `${s.score}%` }} />
                  </div>
                  <span className="font-display text-xl font-semibold text-[#581820]" data-testid={`prospect-score-${s.id}`}>{s.score}</span>
                </div>
                <button
                  data-testid={`prospect-draft-${s.id}`}
                  disabled={draftingId === s.id}
                  onClick={async () => {
                    setDraftingId(s.id);
                    await guarded(async () => {
                      await axios.post(`${API}/outreach/draft`, { school_id: s.id }, auth(token));
                      toast.success(`Draft ready for ${s.name} — open the Outreach tab to review and send.`);
                    }, onAuthError);
                    setDraftingId(null);
                  }}
                  className={btnPrimary}
                >
                  {draftingId === s.id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  Draft Outreach
                </button>
              </div>
            );
          })}
        </div>
      )}

      {view === "cards" && (
      <div className="mt-4 flex items-center justify-between" data-testid="schools-pagination">
        <button
          data-testid="schools-prev-page"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
          className={`${btnGhost} disabled:opacity-40`}
        >
          Prev
        </button>
        <span className="text-xs text-[#8A837B]">
          Page {meta.page} of {meta.pages}
        </span>
        <button
          data-testid="schools-next-page"
          disabled={page >= meta.pages}
          onClick={() => setPage(page + 1)}
          className={`${btnGhost} disabled:opacity-40`}
        >
          Next
        </button>
      </div>
      )}
    </div>
  );
}
